import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Bell,
  ChevronDown,
  Mail,
  MessageSquare,
  Plus,
  UserPlus,
  X,
} from 'lucide-react';

import PrivateLayout from '../layouts/PrivateLayout';
import PickerMenu from '../operations/onboardingSupport/PickerMenu';
import {
  useFetchNotificationSettings,
  useSaveNotificationSettings,
  useSendNotificationSamples,
} from '../../services/adminNotifications';

// Admin-managed notification recipients (Nic 2026-08-12). Two lists, edited
// here instead of DO env vars (which required a rebuild to change):
//   New lead alerts — who hears the 60-second white-glove ping when a
//     partner/referral lead arrives. Empty = the legacy ONBOARDING_ADMIN_*
//     env lists keep working (safe fallback).
//   New signup alerts (2026-10-21) — who hears about every new
//     registration ("New signup: name, source, Rep: first name" since
//     2026-11-01, sent once the signup is on a rep). Empty = the env lists,
//     like New lead alerts.
//   Signed client alerts (2026-11-01): "Signed: name, to Kit" once the
//     rotation picks their specialist at signing; a hot seat redirect sends
//     "Redirected: name, From: Kit, To: Jane". Empty = the env lists.
//   Send me samples: every text above with sample names, to one typed
//     number through the app's own line, so the wording can be seen on a
//     phone before anyone else gets it.
//   (Hand-off alerts retired with Lead Handoffs; the list stays saved.)
// Email recipients are internal app users; SMS recipients are free-typed
// numbers (Jorge likes the texts). admin:system_settings only.

const EMPTY_LISTS = {
  newLead: { userIds: [], phones: [] },
  handoff: { userIds: [], phones: [] },
  signup: { userIds: [], phones: [] },
  signed: { userIds: [], phones: [] },
};

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1/, '');
  return d.length === 10
    ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    : p;
};

const normalizePhoneInput = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return null;
};

const Chip = ({ icon: Icon, label, sub, onRemove }) => (
  <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-2.5 pr-1.5 text-xs font-semibold text-slate-700">
    {Icon ? <Icon size={12} className="shrink-0 text-slate-400" /> : null}
    <span className="truncate">{label}</span>
    {sub ? (
      <span className="truncate font-normal text-slate-400">{sub}</span>
    ) : null}
    <button
      type="button"
      onClick={onRemove}
      title="Remove"
      className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
    >
      <X size={12} />
    </button>
  </span>
);

// Collapsed until opened (Nic 2026-10-23), the header saying who's on it.
const summaryOf = (list) => {
  const e = list.userIds.length;
  const t = list.phones.length;
  if (!e && !t) return 'Nobody yet: the server list gets it';
  return [e ? `${e} email${e === 1 ? '' : 's'}` : null, t ? `${t} text${t === 1 ? '' : 's'}` : null]
    .filter(Boolean)
    .join(' · ');
};

const NotifyListCard = ({
  title,
  hint,
  list,
  internalUsers,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const usersById = new Map(internalUsers.map((u) => [u.userId, u]));
  const remaining = internalUsers.filter(
    (u) => !list.userIds.includes(u.userId),
  );
  const phoneNames = list.phoneNames || {};

  // A name with the number (Nic 2026-10-23), so it's clear whose it is.
  const addPhone = () => {
    const norm = normalizePhoneInput(phoneInput);
    if (!norm) {
      toast.error('Enter a 10-digit US phone number.');
      return;
    }
    const name = nameInput.trim();
    onChange({
      ...list,
      phones: list.phones.includes(norm) ? list.phones : [...list.phones, norm],
      phoneNames: name ? { ...phoneNames, [norm]: name } : phoneNames,
    });
    setPhoneInput('');
    setNameInput('');
  };
  const removePhone = (p) => {
    const { [p]: _gone, ...rest } = phoneNames;
    onChange({ ...list, phones: list.phones.filter((x) => x !== p), phoneNames: rest });
  };

  return (
    <div className="mb-3 rounded-xl bg-white shadow-sm ring-1 ring-slate-900/5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left outline-none hover:bg-slate-50/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
      >
        <Bell size={15} className="shrink-0 text-slate-400" />
        <span className="text-sm font-bold text-slate-800">{title}</span>
        <span className="ml-auto truncate text-xs text-slate-400">{summaryOf(list)}</span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div className="px-4 pb-4">
          <p className="mb-3 text-xs text-slate-400">{hint}</p>

          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <Mail size={12} /> Email
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {list.userIds.map((id) => {
              const u = usersById.get(id);
              return (
                <Chip
                  key={id}
                  label={u ? u.name : 'Unknown user'}
                  onRemove={() =>
                    onChange({
                      ...list,
                      userIds: list.userIds.filter((x) => x !== id),
                    })
                  }
                />
              );
            })}
            {list.userIds.length === 0 ? (
              <span className="text-xs text-slate-300">No email recipients</span>
            ) : null}
          </div>
          <div className="flex max-w-xs">
            <PickerMenu
              label={
                <span className="inline-flex items-center gap-1">
                  <UserPlus size={13} /> Add person…
                </span>
              }
              onPick={(userId) =>
                onChange({ ...list, userIds: [...list.userIds, userId] })
              }
              emptyLabel="Everyone is already on this list"
              triggerClassName="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300"
              options={remaining.map((u) => ({
                value: u.userId,
                label: u.name,
              }))}
            />
          </div>

          <div className="mb-1.5 mt-4 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <MessageSquare size={12} /> Text (SMS)
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {list.phones.map((p) => (
              <Chip
                key={p}
                label={phoneNames[p] || formatPhone(p)}
                sub={phoneNames[p] ? formatPhone(p) : null}
                onRemove={() => removePhone(p)}
              />
            ))}
            {list.phones.length === 0 ? (
              <span className="text-xs text-slate-300">No text recipients</span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addPhone();
              }}
              maxLength={60}
              placeholder="Name (optional)"
              aria-label="Whose number"
              className="w-36 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 outline-none placeholder:text-slate-300 focus:border-slate-400"
            />
            <input
              type="tel"
              aria-label="Phone number"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addPhone();
              }}
              placeholder="(615) 555-1234"
              className="w-44 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 outline-none placeholder:text-slate-300 focus:border-slate-400"
            />
            <button
              type="button"
              onClick={addPhone}
              className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200"
            >
              <Plus size={13} /> Add number
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Send me samples (Nic 2026-11-01): every alert text, with sample names, to
// one typed number through the app's own line. The cards are untouched.
const SamplesCard = () => {
  const [phone, setPhone] = useState('');
  const send = useSendNotificationSamples();
  const digits = phone.replace(/\D/g, '').replace(/^1/, '');
  const doSend = async () => {
    try {
      const data = await send.mutateAsync({ phone });
      toast.success(`${data.sent} sample text${data.sent === 1 ? '' : 's'} sent to ${formatPhone(data.phone)}.`);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not send the samples.');
    }
  };
  return (
    <div className="mt-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
      <div className="text-sm font-semibold text-slate-800">Send me samples</div>
      <p className="mb-3 mt-0.5 text-xs text-slate-400">
        Every alert text above, with sample names, sent to one number through the app&apos;s own line. Nobody on the cards hears it.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(615) 555-0123"
          className="h-9 w-48 rounded-lg border border-slate-200 px-3 text-sm"
        />
        <button
          type="button"
          disabled={digits.length !== 10 || send.isLoading}
          onClick={doSend}
          className="h-9 rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
        >
          {send.isLoading ? 'Sending…' : 'Send samples'}
        </button>
      </div>
    </div>
  );
};

const NotificationSettingsPage = () => {
  const settingsQ = useFetchNotificationSettings();
  const save = useSaveNotificationSettings();
  const [lists, setLists] = useState(null); // local draft; null until loaded
  const server = settingsQ.data?.lists || EMPTY_LISTS;
  const internalUsers = settingsQ.data?.internalUsers || [];

  // Adopt the server value once (and after saves, when clean).
  useEffect(() => {
    if (settingsQ.data && lists === null) {
      setLists(settingsQ.data.lists || EMPTY_LISTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.data]);

  const draft = lists || server;
  const dirty = JSON.stringify(draft) !== JSON.stringify(server);

  const doSave = async () => {
    try {
      const data = await save.mutateAsync({ lists: draft });
      setLists(data.lists);
      settingsQ.refetch();
      toast.success('Notification recipients saved.');
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not save.');
    }
  };

  return (
    <PrivateLayout>
      <div className="mx-auto max-w-2xl py-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="mb-0 text-[17px] font-bold text-slate-800">
              Notification recipients
            </h2>
            <p className="mb-0 text-xs text-slate-400">
              Who gets pinged, managed here instead of environment variables.
            </p>
          </div>
          {dirty ? (
            <button
              type="button"
              disabled={save.isLoading}
              onClick={doSave}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
            >
              {save.isLoading ? 'Saving…' : 'Save changes'}
            </button>
          ) : null}
        </div>

        {settingsQ.isLoading ? (
          <div className="rounded-xl bg-white p-6 text-center text-sm text-slate-400 shadow-sm ring-1 ring-slate-900/5">
            Loading…
          </div>
        ) : (
          <>
            <NotifyListCard
              title="New lead alerts"
              hint="Pinged the moment a partner or referral lead arrives (the 60-second white-glove alert). Leave EMPTY to fall back to the ONBOARDING_ADMIN_EMAIL / ONBOARDING_ADMIN_PHONE environment lists."
              list={draft.newLead}
              internalUsers={internalUsers}
              onChange={(next) => setLists({ ...draft, newLead: next })}
            />
            {/* Nic 2026-10-21: who hears about every new registration,
                instead of the DO environment variable. */}
            <NotifyListCard
              title="New signup alerts"
              hint="Pinged once a new account lands on a Sales Rep: who, where they came from, and the rep. Leave EMPTY to fall back to the ONBOARDING_ADMIN_EMAIL / ONBOARDING_ADMIN_PHONE environment lists."
              list={draft.signup || EMPTY_LISTS.signup}
              internalUsers={internalUsers}
              onChange={(next) => setLists({ ...draft, signup: next })}
            />
            {/* Nic 2026-11-01: who hears "Signed: name, to Kit" once the
                rotation picks their specialist at signing. */}
            <NotifyListCard
              title="Signed client alerts"
              hint="Pinged when someone signs their agreement: who, and the onboarding specialist they went to. A hot seat redirect sends one more line. Leave EMPTY to fall back to the same environment lists."
              list={draft.signed || EMPTY_LISTS.signed}
              internalUsers={internalUsers}
              onChange={(next) => setLists({ ...draft, signed: next })}
            />
            <SamplesCard />
          </>
        )}
      </div>
    </PrivateLayout>
  );
};

export default NotificationSettingsPage;
